import { type UiIconName } from "@dpa-id-components/dpa-shared-components/src/components/UiIcon/UiIcon.vue";
import { defineStore } from "pinia";
import { v4 as uuid } from "uuid";
import { ref, type VNode, watchEffect } from "vue";

interface IToast {
  id: string;
  message: string;
  icon?: UiIconName;
  renderFn?: VNode;
  timeout?: "none" | number;
  urn?: string;
}

type Toast = Omit<IToast, "id">;

const defaultTimeout = 2000;

const createMessage = ({
  message,
  icon = undefined,
  renderFn = undefined,
  timeout = defaultTimeout,
  urn = undefined,
}: Toast): IToast => {
  return {
    id: uuid(),
    message,
    icon,
    renderFn,
    timeout,
    urn,
  };
};

export const useToaster = defineStore("toasterStore", () => {
  const toasts = ref<IToast[]>([]);
  const shown = ref(false);

  const showToaster = () => {
    shown.value = true;
  };
  const hideToaster = () => {
    shown.value = false;
    toasts.value = [];
  };

  const addToast = (payload: Toast) => {
    const toast = createMessage(payload);
    toasts.value.push(toast);

    if (typeof toast.timeout === "number") {
      setTimeout(() => {
        toasts.value = toasts.value.filter((msg) => msg.id !== toast.id);
      }, toast.timeout);
    }

    return toast;
  };

  const removeToast = (id: string) => {
    toasts.value = toasts.value.filter((toast) => {
      return toast.urn !== id && toast.id !== id;
    });
  };

  watchEffect(() => {
    if (toasts.value.length === 0) {
      hideToaster();
    } else {
      showToaster();
    }
  });

  return {
    shown,
    showToaster,
    hideToaster,
    toasts,
    addToast,
    removeToast,
  };
});
